/** Most of the complicated stuff in this file is to work around known bugs and limitations of 3rd-party libraries.
    It would be great to delete a lot of code here AFTER the 3rd-party libs are better able to handle things. */
import { compact} from 'lodash';

// 3rd-party wrapped
import { DEFAULT_CLIPPING_LIBRARY, clippingLibrary } from './constants';
import { difference as turfDiff } from '3rd-party-wrappers/turf';
import { diff as martinezDiff } from '3rd-party-wrappers/martinez-polygon-clipping';
import { diff as clipperLibDiff } from '3rd-party-wrappers/clipper-lib';
import { difference as cglDifference } from '@tcc/cgl';

// Util
import { getFeature, getGeometry, polygonArrayToXPolygon, xPolygonToPolygonArray } from 'geometry/geojson/convert';
import { doesXPolygonHaveArea } from 'geometry/geojson/area';
import { isValidXPolygon } from 'geometry/geojson/validator';
import { workerLogError } from 'scripts/editZones.worker';
import { roundGeometry } from 'util/round';

/** @return Polygons that were not in membership of resultPolygons before. resultPolygons will be mutated
      to contain other subtractions. */
const _subtractPolygonsPassMartinez = ({resultPolygons, subtractPolygons}) => {
  let fatalError = false;
  const newPolygons = [];

  // For each polygon in the result...
  resultPolygons.forEach((resultPolygon, resultI) => {
    if (!resultPolygon || fatalError) { return; } // !resultPolygon means the polygon's entire area was subtracted away.

    // For each polygon in subtraction array, remove it from from the result polygon.
    subtractPolygons.forEach((subtractPolygon) => {
      const newCoordinates = martinezDiff(resultPolygon.coordinates, subtractPolygon.coordinates);
      if (newCoordinates === undefined) { fatalError = true; return; }
      if (!newCoordinates || !newCoordinates.length) {
        resultPolygons[resultI] = null;
        return;
      }
      resultPolygon.coordinates = newCoordinates[0];

      if (newCoordinates.length > 1) { // The subtraction split the polygon into a multi-polygon.
        newCoordinates.forEach((newPolygonCoords, newPolygonI) => {
          if (newPolygonI === 0) { return; }
          newPolygons.push({type:'Polygon', coordinates:newPolygonCoords});
        });
      }
    });
  });

  return newPolygons;
};

const _calculateDifferenceMartinez = (geometry1, geometry2) => {
  // Split MultiPolygons into individual polygons to get around a Martinez error.
  let toDoPolygons = xPolygonToPolygonArray(geometry1);
  let donePolygons = [];
  const subtractPolygons = xPolygonToPolygonArray(geometry2);

  /* Subtract polygons in subtraction array from the todo array. New polygons get created in a pass
     if a subtraction divides a polygon into multiple pieces. When that happens, another pass is
     performed to make sure those new polygons also have correct subtactions made. */
  while (toDoPolygons.length) {
    const newPolygons = _subtractPolygonsPassMartinez({resultPolygons:toDoPolygons, subtractPolygons});
    if (newPolygons === undefined) { return undefined; } // Error that was already logged.
    donePolygons = donePolygons.concat(toDoPolygons);
    toDoPolygons = newPolygons;
  }

  // Combine the results into one Polygon, MultiPolygon, or null (all area of shape removed).
  const compactedResult = compact(donePolygons);
  return (compactedResult.length) ? polygonArrayToXPolygon(compactedResult) : null;
};

const _calculateDifferenceTurf = (geometry1, geometry2) => {
  const differenceFeature = turfDiff(getFeature(geometry1), getFeature(geometry2));
  if (differenceFeature === undefined) { return undefined; } // API error already logged.
  if (!differenceFeature) { return null; } // This is coupled to Turf 3.x. For later versions, I think the expected return is different.

  // A shape was returned from difference().
  const xPolygon = getGeometry(differenceFeature);
  if (xPolygon === undefined) { return undefined; }

  return xPolygon;
};

const _calculateDifferenceClipperLib = (xPolygon1, xPolygon2) => clipperLibDiff(xPolygon1, xPolygon2);

/** Calculate the differing shape(s) between two geoJsons.
    @param geoJson1 Feature, Polygon, or MultiPolygon
    @param geoJson2 Feature, Polygon, or MultiPolygon
    @param useLibraries An array with each element indicating library to use for clipping operations. Will be tried in
       in order.
    @returns Polygon or MultiPolygon containing difference of geoJson1/2. Or null, if no difference. */
export const calculateDifference = (geoJson1, geoJson2, useLibraries = DEFAULT_CLIPPING_LIBRARY) => {
  const geometry1 = roundGeometry(getGeometry(geoJson1));
  const geometry2 = roundGeometry(getGeometry(geoJson2));

  if (!isValidXPolygon(geometry1) || !isValidXPolygon(geometry2)) // TODO - remove after done tshooting a certain bug
  { workerLogError({message:'calculateDifference() bad params', context:{geoJson1, geoJson2}}); }

  // Handle 2 trivial cases and also prevent errors in clipping functions.
  if (!doesXPolygonHaveArea(geometry1)) { return null; }
  if (!doesXPolygonHaveArea(geometry2)) { return geometry1; }

  // Aware of the absurdity of this, and hope to one day delete it down to one call to a trusted lib.
  let xPolygon;
  useLibraries.forEach(library => {
    if (xPolygon === undefined) {
      if (library === 'MFOGEL') {
        const coordinates = cglDifference(workerLogError, geometry1.coordinates, geometry2.coordinates);
        if (coordinates.length === 0) {
          xPolygon = null;
        } else if (coordinates.length === 1) {
          xPolygon = { type: 'Polygon', coordinates: coordinates[0] };
        } else {
          xPolygon = { type: 'MultiPolygon', coordinates };
        }
      }
      if (library === clippingLibrary.TURF) {
        xPolygon = _calculateDifferenceTurf(geometry1, geometry2);
      } else if (library === clippingLibrary.MARTINEZ) {
        xPolygon = _calculateDifferenceMartinez(geometry1, geometry2);
      } else if (library === clippingLibrary.CLIPPERLIB) {
        xPolygon = _calculateDifferenceClipperLib(geometry1, geometry2);
      }
    }
  });

  if (xPolygon !== undefined) { return xPolygon; }

  throw new Error('calculateDifference() failed'); // Specific reason already logged.
};

export const unitTestPrivate = {
  _calculateDifferenceMartinez,
  _subtractPolygonsPassMartinez
};
